import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectedItemsSelector } from '@air/redux-selected-items';

import { buildSelectedItemsSummary } from '~/components/v2/components/shared/selectionBar/utils/buildSelectedItemsSummary';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';

export type UseSelectedItemsSummaryParams = {
  selectAllTotalCount?: number | null;
  omitBoardName?: boolean;
};

export type UseSelectedItemsSummaryResult = {
  /**
   * Single-line form — "5 items selected from Brand Assets". The V1 bar renders this verbatim; the
   * agent bar renders the parts below instead so it can weight them separately.
   */
  summary: string;
  /** "5 items", "3 canvases", "All 1,200 items". */
  countLabel: string;
  /** Where the selection came from, when that is worth naming on its own. */
  scopeLabel?: string;
};

export const useSelectedItemsSummary = ({
  selectAllTotalCount,
  omitBoardName,
}: UseSelectedItemsSummaryParams): UseSelectedItemsSummaryResult => {
  const selectedItems = useSelector(selectedItemsSelector);
  const { crossLocationSelectionEnabled } = useFeatureFlags();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);

  return useMemo(
    () =>
      buildSelectedItemsSummary({
        selectedItems,
        crossLocationSelectionEnabled,
        selectAll,
        selectAllTotalCount,
        omitBoardName,
      }),
    [selectedItems, crossLocationSelectionEnabled, selectAll, omitBoardName, selectAllTotalCount],
  );
};
